/**
 * User Management Service
 * API calls for user, role, permission, hierarchy, and reporting management
 */

import apiService from './api.service';

export interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  role: string;
  roleId?: {
    _id: string;
    name: string;
    key: string;
  };
  reportingTo?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  isActive: boolean;
  emailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  reports?: User[];
}

/** Three nested access tiers per module — 'full' implies 'edit' implies 'read'.
 *  Matches backend Role.model.ts's PermissionAccessLevel exactly. */
export type PermissionAccessLevel = 'read' | 'edit' | 'full';

export interface ModulePermission {
  module: string;
  accessLevel: PermissionAccessLevel;
}

/** Display-list shape only (GET /roles/:id/permissions) — 'none' is a
 *  display-only sentinel for "no permissions entry exists for this module
 *  yet," never a storable accessLevel. Also what PUT expects back to REVOKE
 *  a module (there's no on-disk "no access" tier, only presence/absence). */
export interface DisplayModulePermission {
  module: string;
  accessLevel: PermissionAccessLevel | 'none';
}

export interface Role {
  _id: string;
  name: string;
  key: string;
  description?: string;
  parentRoleId?: {
    _id: string;
    name: string;
    key: string;
  };
  level: number;
  order: number;
  permissions: ModulePermission[];
  isSystemRole: boolean;
  isActive: boolean;
  children?: Role[];
  userCount?: number;
}

export interface Module {
  key: string;
  name: string;
  enabled: boolean;
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  role?: string;
  roleId?: string;
  reportingTo?: string;
  // No isActive (→ setUserStatus) and no password (admins can't set a member's
  // password) — the backend refuses both on update.
}

export interface CreateRoleData {
  name: string;
  key: string;
  description?: string;
  parentRoleId?: string;
  level?: number;
  order?: number;
  permissions?: ModulePermission[];
}

export interface UpdateRoleData {
  name?: string;
  description?: string;
  parentRoleId?: string;
  level?: number;
  order?: number;
  permissions?: ModulePermission[];
  isActive?: boolean;
}

class UserManagementService {
  // User methods
  async getUsers(
    params: {
      page?: number;
      limit?: number;
      search?: string;
      role?: string;
      isActive?: boolean;
    } = {}
  ) {
    const response = await apiService.get<{ success: boolean; data: User[]; meta: any }>(
      '/admin/user-management/users',
      { params }
    );
    return {
      data: response.data || [],
      meta: response.meta || { total: 0, page: 1, limit: 20, totalPages: 0 },
    };
  }

  async getUserById(userId: string) {
    const response = await apiService.get<{ success: boolean; data: User }>(
      `/admin/user-management/users/${userId}`
    );
    return response.data;
  }

  async updateUser(userId: string, userData: UpdateUserData) {
    const response = await apiService.put<{
      success: boolean;
      data: { message: string; user: User };
    }>(`/admin/user-management/users/${userId}`, userData);
    return response.data;
  }

  /** Disable / enable — the only way to change status. Disabling also signs them out everywhere. */
  async setUserStatus(userId: string, isActive: boolean) {
    const response = await apiService.patch<{
      success: boolean;
      data: { message: string; user: Pick<User, '_id' | 'isActive'> };
    }>(`/admin/user-management/users/${userId}/status`, { isActive });
    return response.data;
  }

  async deleteUser(userId: string) {
    const response = await apiService.delete<{ success: boolean; data: { message: string } }>(
      `/admin/user-management/users/${userId}`
    );
    return response.data;
  }

  // Role methods
  async getRoles() {
    const response = await apiService.get<{ success: boolean; data: Role[] }>(
      '/admin/user-management/roles'
    );
    return response.data || [];
  }

  async getRoleById(roleId: string) {
    const response = await apiService.get<{ success: boolean; data: Role }>(
      `/admin/user-management/roles/${roleId}`
    );
    return response.data;
  }

  async createRole(roleData: CreateRoleData) {
    const response = await apiService.post<{
      success: boolean;
      data: { message: string; role: Role };
    }>('/admin/user-management/roles', roleData);
    return response.data;
  }

  async updateRole(roleId: string, roleData: UpdateRoleData) {
    const response = await apiService.put<{
      success: boolean;
      data: { message: string; role: Role };
    }>(`/admin/user-management/roles/${roleId}`, roleData);
    return response.data;
  }

  async deleteRole(roleId: string) {
    const response = await apiService.delete<{ success: boolean; data: { message: string } }>(
      `/admin/user-management/roles/${roleId}`
    );
    return response.data;
  }

  async getRoleHierarchy() {
    const response = await apiService.get<{ success: boolean; data: Role[] }>(
      '/admin/user-management/roles/hierarchy/tree'
    );
    return response.data || [];
  }

  // Reporting methods
  async getUserReportingTree(userId: string) {
    const response = await apiService.get<{ success: boolean; data: any }>(
      `/admin/user-management/reporting-tree/${userId}`
    );
    return response.data;
  }

  // Module permission methods
  async getModules() {
    const response = await apiService.get<{ success: boolean; data: Module[] }>(
      '/admin/user-management/modules'
    );
    return response.data || [];
  }

  async getRolePermissions(roleId: string) {
    const response = await apiService.get<{
      success: boolean;
      data: { roleId: string; roleName: string; permissions: DisplayModulePermission[] };
    }>(`/admin/user-management/roles/${roleId}/permissions`);
    return response.data;
  }

  /** Pass accessLevel:'none' to revoke a module entirely (no on-disk "no access"
   *  tier — presence/absence in the stored array is what matters server-side). */
  async updateRolePermissions(roleId: string, permissions: DisplayModulePermission[]) {
    const response = await apiService.put<{
      success: boolean;
      data: { message: string; role: Role };
    }>(`/admin/user-management/roles/${roleId}/permissions`, { permissions });
    return response.data;
  }
}

export default new UserManagementService();
